import { TenantPreview, setGroups, setTenant, setUser } from "@/libs/auth/auth.slice";
import { PermissionMapping } from "@/permissions";
import { WhoAmIRead } from "@/services/generatedApi";
import { useAppDispatch, useAppSelector } from "@/services/store";
import * as React from "react";
import { Dispatch, SetStateAction } from "react";
import * as R from "remeda";
import { useLocalStorage } from "usehooks-ts";
import { userIdSelector } from "./auth.selectors";

/**
 * Mostly a helper function to get the tenant from the store.
 * avoids asserting the type every time.
 */
export function useTenantSafe(): TenantPreview {
  const tenant = useAppSelector(state => state.auth.tenant);
  return tenant as TenantPreview;
}

export function useUserId(): string {
  return useAppSelector(userIdSelector);
}

/**
 * A hook to get the tenant from local storage.
 * This is used to persist the tenant across sessions for super admin users
 */
export function useLocalStorageTenant(): [TenantPreview | null, Dispatch<SetStateAction<TenantPreview | null>>] {
  const [tenant, setTenant] = useLocalStorage<TenantPreview | null>("tenant", null, {
    serializer: JSON.stringify,
    deserializer: JSON.parse,
  });
  return [tenant, setTenant];
}

/**
 * Loads the tenant information from the retrieved whoAmI data into state.
 * Handles the superadmin case where the tenant can be stored in local storage.
 * @param data
 * @param onSuccess
 */
export function useUserLoader(data: WhoAmIRead | undefined, onSuccess: () => void) {
  const dispatch = useAppDispatch();
  const [storedTenant, _] = useLocalStorageTenant();
  const tenantLoaded = React.useRef(false);

  React.useEffect(() => {
    if (R.isNullish(data) || tenantLoaded.current) {
      return;
    }
    dispatch(setUser(data));
    dispatch(setGroups(data.groups));
    const { tenant } = data;
    if (!tenant) {
      onSuccess();
      return;
    }
    // Since only super_admin can have multiple tenants
    // we're best off avoiding any local storage changes to non super_admin users
    if (data.groups.includes(PermissionMapping.super_admin)) {
      dispatch(setTenant(storedTenant || tenant));
    } else {
      // Regular user, continue as usual
      dispatch(setTenant(tenant));
    }
    tenantLoaded.current = true;
    onSuccess();
  }, [data, storedTenant, tenantLoaded]);
}
